import { Box, ProfilePhoto, Text } from "@minervaproject/fds";
import { unwrapResult } from "@reduxjs/toolkit";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDrag } from "react-dnd";

import { TalkTimeOverlay } from "components/participant_bar/talk_time_overlay";
import { ParticipantInfoMenu } from "components/shared/participant_info_menu";
import { ParticipantInfoMenuIcon } from "components/shared/participant_info_menu_icon";
import { SignalsAndReactions } from "components/shared/signals_and_reactions";
import { createNoClassroomChangeToast } from "components/shared/toasts";
import { useCapability, useUserIdsWithCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { collect, useDragHandler } from "hooks/use_drag_handler";
import {
  PROFILE_PROPERTIES,
  USER_PICKER_PROPERTIES,
  useUsersProperties,
} from "hooks/use_user_properties";
import {
  addUserToParticipantBarAndReplaceOldUser,
  addUserToParticipantBarAndToBestPaneOnStage,
  addUserToParticipantBarAndToSpecificPaneOnStage,
} from "shared/reducers/classroom";
import {
  CapabilityType,
  DraggableItemType,
  KeyboardShortcutSignalType,
  SidebarPanelType,
} from "shared/types";
import { RoleType, UserType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store";
import { setKeyboardShortcutSignal, toggleSidebarPanel } from "store/reducers/local";
import { selectActiveBreakoutSessionId } from "store/selectors/classroom";
import { stringComparatorGenerator } from "utils/string";
import {
  computeInitials,
  getNameForSorting,
  getRoleAndNameForSorting,
  longDisplayName,
  profilePhotoUrl,
  roleForDisplay,
} from "utils/user";

function DirectoryUser({
  user,
  previousUser,
  canAddUser,
  handleUser,
  staffDirectory,
}: {
  user: UserType;
  previousUser: UserType | undefined;
  canAddUser: boolean;
  handleUser: (args: { userId?: string; paneIndex?: number }) => void;
  staffDirectory?: boolean;
}) {
  const currentUserCanControlClassroom = useCapability(CapabilityType.CAN_CONTROL_CLASSROOM);
  const displayName = longDisplayName(user);
  const firstLetter = displayName && displayName[0].toUpperCase();
  const previousDisplayName = previousUser && longDisplayName(previousUser);
  const previousFirstLetter = previousDisplayName && previousDisplayName[0].toUpperCase();
  const role = roleForDisplay(user);
  const previousRole = previousUser && roleForDisplay(previousUser);
  const [isHovered, setIsHovered] = useState(false);
  const [showParticipantInfoMenu, setShowParticipantInfoMenu] = useState(false);

  const cameraError = useAppSelector(classroomSelectors.selectUserCameraError(user.id));
  const micError = useAppSelector(classroomSelectors.selectUserMicError(user.id));
  const avAvailable = useAppSelector(classroomSelectors.selectUserHasAvailableAv(user.id));
  const avIssue = !!micError || !avAvailable;
  const poorConnection = useAppSelector(classroomSelectors.selectUserHasPoorConnection(user.id));

  const [{ isDragging }, drag] = useDrag({
    type: DraggableItemType.DirectoryUser,
    item: {
      directoryCallback: ({ userId, paneIndex }: { userId: string; paneIndex: number }) => {
        handleUser({ userId, paneIndex });
      },
    },
    collect,
  });
  useDragHandler(isDragging);

  const handleClick = () => {
    handleUser({});
  };

  const profilePhotoAndName = (
    <div className="flex flex-none">
      <div className="relative">
        <ProfilePhoto
          className="ml2"
          size="sm"
          src={profilePhotoUrl(user)}
          initials={computeInitials(user)}
        />
        <div
          className="absolute"
          style={{
            width: "30px",
            height: "30px",
            left: "3px",
            top: "-4px",
          }}
        >
          <SignalsAndReactions userId={user.id} size={26} noAnimation />
        </div>
        {currentUserCanControlClassroom && (
          <div
            className="absolute top-0 circle overflow-hidden"
            style={{
              width: "24px",
              height: "24px",
              left: "8px",
            }}
          >
            <TalkTimeOverlay userId={user.id} noIcon />
          </div>
        )}
      </div>
      <div className="body-s truncate ml2" style={{ maxWidth: "190px" }}>
        {displayName}
      </div>
    </div>
  );

  return (
    <div className="flex-none">
      {staffDirectory ? (
        role !== previousRole ? (
          <div className="text-black-tint-70 pb2 pt4" style={{ paddingLeft: "40px" }}>
            {role}
          </div>
        ) : null
      ) : firstLetter !== previousFirstLetter ? (
        <div className="text-black-tint-70 pb2 pt4" style={{ paddingLeft: "40px" }}>
          {firstLetter}
        </div>
      ) : null}
      <div
        className="flex items-center w-full pt1 pr2 relative border-box"
        style={!user.is_present ? { opacity: "0.5" } : {}}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex items-center justify-between w-full">
          {canAddUser ? (
            <button
              type="button"
              className={clsx("button-reset text-white flex flex-auto pb1", {
                "cursor-pointer": user.is_present,
              })}
              onClick={handleClick}
              ref={drag}
              data-testid={`directory-user-${user.id}`}
            >
              {profilePhotoAndName}
            </button>
          ) : (
            <div className="flex flex-none pb1">{profilePhotoAndName}</div>
          )}
          {user.is_present &&
            (showParticipantInfoMenu ||
              isHovered ||
              avIssue ||
              micError ||
              cameraError ||
              poorConnection) && (
              <button
                type="button"
                className="button-reset cursor-pointer pb1"
                aria-label="Participant info menu"
                onClick={() => setShowParticipantInfoMenu(!showParticipantInfoMenu)}
              >
                <ParticipantInfoMenuIcon
                  avIssue={avIssue}
                  micError={!!micError}
                  cameraError={!!cameraError}
                  poorConnection={poorConnection}
                />
              </button>
            )}
        </div>
        {showParticipantInfoMenu && (
          <div onMouseLeave={() => setShowParticipantInfoMenu(false)}>
            <ParticipantInfoMenu userId={user.id} isDirectory />
          </div>
        )}
      </div>
    </div>
  );
}

function DirectoryUsers({
  users,
  canAddUser,
  handleUser,
  staffDirectory,
}: {
  users: UserType[];
  canAddUser: boolean;
  handleUser: (user: UserType) => (args: { userId?: string; paneIndex?: number }) => void;
  staffDirectory?: boolean;
}) {
  return (
    <>
      {users.map((user, index) => (
        <DirectoryUser
          key={user.id}
          user={user}
          previousUser={users[index - 1]}
          canAddUser={canAddUser}
          handleUser={handleUser(user)}
          staffDirectory={staffDirectory}
        />
      ))}
    </>
  );
}

function Directory() {
  const dispatch = useAppDispatch();
  const keyboardShortcutSignal = useAppSelector(localSelectors.selectKeyboardShortcutSignal());
  const [selectedTab, setSelectedTab] = useState("present");
  const users = Object.values(
    useUsersProperties(PROFILE_PROPERTIES.concat(USER_PICKER_PROPERTIES, "is_recording_agent")),
  );
  users.sort(stringComparatorGenerator(getNameForSorting));

  const canParticipateInClassUserIds = useUserIdsWithCapability(
    CapabilityType.CAN_PARTICIPATE_IN_CLASS,
  );
  const canControlClassroomUserIds = useUserIdsWithCapability(CapabilityType.CAN_CONTROL_CLASSROOM);
  const canVisitBreakoutsUserIds = useUserIdsWithCapability(CapabilityType.CAN_VISIT_BREAKOUTS);
  const isVisibleInClassUserIds = useUserIdsWithCapability(CapabilityType.IS_VISIBLE_IN_CLASS);
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());

  const students = users.filter(({ id }) => canParticipateInClassUserIds.includes(id));
  const presentStudents = students.filter(({ is_present }) => is_present);
  const absentStudents = students.filter(({ is_present }) => !is_present);
  const staff = users.filter(({ id, is_present, is_recording_agent, role }) => {
    if (is_recording_agent) return false;
    const canControlClassroom = canControlClassroomUserIds.includes(id);
    const canVisitBreakouts = canVisitBreakoutsUserIds.includes(id);
    const isVisibleInClass = isVisibleInClassUserIds.includes(id);
    const isStaff =
      ((canControlClassroom || canVisitBreakouts) && isVisibleInClass) ||
      role === RoleType.Captioner;
    const showPresentObservers = canControlClassroomUserIds.includes(currentUserId);
    if (showPresentObservers) {
      return isStaff || (!isVisibleInClass && is_present);
    }
    return isStaff;
  });
  staff.sort(stringComparatorGenerator(getRoleAndNameForSorting));

  const nPresent = presentStudents.length;
  const nAbsent = absentStudents.length;
  const nStaff = staff.length;

  useEffect(() => {
    if (keyboardShortcutSignal === KeyboardShortcutSignalType.ToggleAbsentStudents) {
      if (selectedTab !== "absent") {
        setSelectedTab("absent");
      } else {
        // Close the directory sidebar if absent students are already visible
        dispatch(toggleSidebarPanel({ panel: SidebarPanelType.Directory }));
      }
      dispatch(setKeyboardShortcutSignal({ signal: KeyboardShortcutSignalType.None }));
    }
  }, [dispatch, keyboardShortcutSignal, selectedTab]);

  const isInPerson = useAppSelector(classroomSelectors.selectIsInPersonClass());
  const activeBreakoutSessionId = useAppSelector(selectActiveBreakoutSessionId());

  const canAddUser =
    !isInPerson && !activeBreakoutSessionId && canControlClassroomUserIds.includes(currentUserId);

  const handleUser = (user: UserType) => async (args: { userId?: string; paneIndex?: number }) => {
    if (canAddUser && user.is_present && isVisibleInClassUserIds.includes(user.id)) {
      if (args.userId !== undefined) {
        const actionResult = await dispatch(
          addUserToParticipantBarAndReplaceOldUser({
            userId: user.id,
            userIdToReplace: args.userId,
          }),
        );
        const result = unwrapResult(actionResult);
        if (!result.updated) createNoClassroomChangeToast(result.msg);
      } else if (args.paneIndex !== undefined) {
        const actionResult = await dispatch(
          addUserToParticipantBarAndToSpecificPaneOnStage({
            userId: user.id,
            paneIndex: args.paneIndex,
          }),
        );
        const result = unwrapResult(actionResult);
        if (!result.updated) createNoClassroomChangeToast(result.msg);
      } else {
        const actionResult = await dispatch(
          addUserToParticipantBarAndToBestPaneOnStage({ userId: user.id }),
        );
        const result = unwrapResult(actionResult);
        if (!result.updated) createNoClassroomChangeToast(result.msg);
      }
    }
  };

  return (
    <div className="flex flex-auto sidebar-width border-box p2">
      <div className="flex flex-column flex-auto">
        <Text className="body-l flex-none" kind="h2" p={2}>
          Class Directory
        </Text>
        <Box className="fds-tabs--dark flex-none" inline pt={2} px={2}>
          <button
            type="button"
            className={clsx("tab", { "tab--active": selectedTab === "present" })}
            onClick={() => setSelectedTab("present")}
          >
            {`Present (${nPresent})`}
          </button>
          <button
            type="button"
            className={clsx("tab", { "tab--active": selectedTab === "absent" })}
            onClick={() => setSelectedTab("absent")}
          >
            {`Absent (${nAbsent})`}
          </button>
          <button
            type="button"
            className={clsx("tab", { "tab--active": selectedTab === "staff" })}
            onClick={() => setSelectedTab("staff")}
          >
            {`Staff (${nStaff})`}
          </button>
        </Box>
        <div className="flex flex-auto">
          <div
            className="flex flex-column flex-auto vertical-scroll"
            style={{ overflowX: "hidden" }}
          >
            {selectedTab === "present" ? (
              <DirectoryUsers
                users={presentStudents}
                canAddUser={canAddUser}
                handleUser={handleUser}
              />
            ) : null}
            {selectedTab === "absent" ? (
              <DirectoryUsers
                users={absentStudents}
                canAddUser={canAddUser}
                handleUser={handleUser}
              />
            ) : null}
            {selectedTab === "staff" ? (
              <DirectoryUsers
                users={staff}
                canAddUser={canAddUser}
                handleUser={handleUser}
                staffDirectory
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export { Directory, DirectoryUser, DirectoryUsers };
